import { createJSONStorage } from 'zustand/middleware'
import {
  decryptPersistedValue,
  encryptPersistedValue,
  generatePersistenceKey,
  isEncryptedEnvelope,
  isPersistenceCryptoAvailable,
  type EncryptedEnvelope,
} from '@/shared/lib/persistenceCrypto'

const fallbackStorage: Storage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
  clear: () => undefined,
  key: () => null,
  length: 0,
}

export const localStorageJSON = createJSONStorage(() =>
  typeof window !== 'undefined' ? window.localStorage : fallbackStorage,
)

type AsyncStateStorage = {
  getItem: (name: string) => string | null | Promise<string | null>
  setItem: (name: string, value: string) => void | Promise<void>
  removeItem: (name: string) => void | Promise<void>
}

/*
 * Stores persistidos no IndexedDB (`synap` / `zustand`) ficam cifrados com AES-GCM e uma
 * chave não extraível por usuário (ver persistenceCrypto.ts). As chaves ficam em outro
 * banco (`synap-keys`), como CryptoKey. Sem usuário ativo nada é lido nem gravado.
 */
const IDB_DB_NAME = 'synap'
const IDB_STORE_NAME = 'zustand'
export const PERSISTENCE_KEYS_DB_NAME = 'synap-keys'
const KEYS_STORE_NAME = 'keys'

const openDb = (name: string, storeName: string): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'))
      return
    }

    const request = indexedDB.open(name, 1)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('indexedDB open failed'))
  })

let idbDbPromise: Promise<IDBDatabase> | null = null
let keysDbPromise: Promise<IDBDatabase> | null = null

const openIdb = (): Promise<IDBDatabase> => {
  if (!idbDbPromise) {
    idbDbPromise = openDb(IDB_DB_NAME, IDB_STORE_NAME)
    idbDbPromise.catch(() => {
      idbDbPromise = null
    })
  }
  return idbDbPromise
}

const openKeysDb = (): Promise<IDBDatabase> => {
  if (!keysDbPromise) {
    keysDbPromise = openDb(PERSISTENCE_KEYS_DB_NAME, KEYS_STORE_NAME)
    keysDbPromise.catch(() => {
      keysDbPromise = null
    })
  }
  return keysDbPromise
}

const requestToPromise = <T>(db: IDBDatabase, storeName: string, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode)
    const req = run(tx.objectStore(storeName))
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error(`indexedDB ${mode} failed`))
  })

/* ---------- usuário ativo ---------- */

/**
 * `undefined` = ainda não informado (a sessão não inicializou): leituras e gravações
 * esperam. `null` = sem usuário: leitura é cache miss e gravação é descartada.
 */
let activeUserId: string | null | undefined
let activeUserWaiters: Array<() => void> = []

/** Chamado pela sessão sempre que o usuário muda (inclusive para null no logout). */
export const setPersistenceUser = (userId: string | null): void => {
  activeUserId = userId || null
  const waiters = activeUserWaiters
  activeUserWaiters = []
  waiters.forEach((resolve) => resolve())
}

const resolveActiveUser = async (): Promise<string | null> => {
  if (activeUserId === undefined) {
    await new Promise<void>((resolve) => activeUserWaiters.push(resolve))
  }
  return activeUserId ?? null
}

/* ---------- chaves por usuário ---------- */

const keyCache = new Map<string, Promise<CryptoKey>>()

const loadOrCreateUserKey = async (userId: string): Promise<CryptoKey> => {
  const db = await openKeysDb()
  const existing = await requestToPromise<unknown>(db, KEYS_STORE_NAME, 'readonly', (s) => s.get(userId))
  if (existing && typeof existing === 'object' && 'algorithm' in existing) return existing as CryptoKey
  const key = await generatePersistenceKey()
  await requestToPromise(db, KEYS_STORE_NAME, 'readwrite', (s) => s.put(key, userId))
  return key
}

const getUserKey = (userId: string): Promise<CryptoKey> => {
  let pending = keyCache.get(userId)
  if (!pending) {
    pending = loadOrCreateUserKey(userId)
    keyCache.set(userId, pending)
    pending.catch(() => keyCache.delete(userId))
  }
  return pending
}

/* ---------- registros ---------- */

const idbGetRaw = async (key: string): Promise<unknown> => {
  const db = await openIdb()
  return await requestToPromise<unknown>(db, IDB_STORE_NAME, 'readonly', (s) => s.get(key))
}

const isQuotaExceededError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false
  const e = err as { name?: string; code?: number }
  return (
    e.name === 'QuotaExceededError' ||
    e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    e.code === 22 ||
    e.code === 1014
  )
}

const OUTBOX_STORAGE_KEY = 'synap-offline-outbox'

const SYNAP_INDEXEDDB_KEYS = [
  'synap-patient-store',
  'synap-exam-store',
  'synap-calendar-store',
  'synap-clinical-document-store',
  OUTBOX_STORAGE_KEY,
]

/**
 * Registros em texto puro de antes da criptografia: caches são descartados; a fila
 * offline é recifrada para o usuário ativo, porque guarda operações clínicas ainda não
 * sincronizadas (RW-S01).
 */
const LEGACY_PLAINTEXT_KEYS_TO_MIGRATE = new Set([OUTBOX_STORAGE_KEY])

const idbDel = async (key: string): Promise<void> => {
  const db = await openIdb()
  await requestToPromise(db, IDB_STORE_NAME, 'readwrite', (s) => s.delete(key))
}

const idbPut = async (key: string, value: EncryptedEnvelope): Promise<void> => {
  const db = await openIdb()
  const doPut = () => requestToPromise(db, IDB_STORE_NAME, 'readwrite', (s) => s.put(value, key))

  try {
    await doPut()
  } catch (err) {
    if (isQuotaExceededError(err)) {
      try {
        await Promise.all(SYNAP_INDEXEDDB_KEYS.map((k) => idbDel(k)))
        await doPut()
        return
      } catch {
        /* eviction ou retry falhou; deixa o erro propagar */
      }
    }
    throw err
  }
}

const writeEncrypted = async (userId: string, name: string, value: string): Promise<void> => {
  const key = await getUserKey(userId)
  const envelope = await encryptPersistedValue(key, userId, name, value)
  /* O usuário pode ter saído enquanto cifrava: não gravar dado dele depois do logout. */
  if (activeUserId !== userId) return
  await idbPut(name, envelope)
}

const readDecrypted = async (name: string): Promise<string | null> => {
  const userId = await resolveActiveUser()
  if (!userId || !isPersistenceCryptoAvailable()) return null
  const raw = await idbGetRaw(name)
  if (raw === undefined || raw === null) return null

  if (typeof raw === 'string') {
    await idbDel(name).catch(() => undefined)
    if (!LEGACY_PLAINTEXT_KEYS_TO_MIGRATE.has(name)) return null
    await writeEncrypted(userId, name, raw).catch(() => undefined)
    return raw
  }

  if (!isEncryptedEnvelope(raw) || raw.uid !== userId) {
    await idbDel(name).catch(() => undefined)
    return null
  }

  try {
    const key = await getUserKey(userId)
    return await decryptPersistedValue(key, name, raw)
  } catch {
    /* Chave trocada/apagada ou registro corrompido: cache miss e limpa o registro. */
    await idbDel(name).catch(() => undefined)
    return null
  }
}

/**
 * Logout: apaga a chave do usuário e todo registro cifrado com ela. Sem a chave, o que
 * sobrar (ex.: gravação em voo) não decifra e é descartado na próxima leitura.
 */
export const deletePersistenceUserData = async (userId: string): Promise<void> => {
  if (!userId) return
  keyCache.delete(userId)
  try {
    const keysDb = await openKeysDb()
    await requestToPromise(keysDb, KEYS_STORE_NAME, 'readwrite', (s) => s.delete(userId))
  } catch {
    /* IDB indisponível */
  }
  try {
    const db = await openIdb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite')
      const req = tx.objectStore(IDB_STORE_NAME).openCursor()
      req.onsuccess = () => {
        const cursor = req.result
        if (!cursor) return
        if (isEncryptedEnvelope(cursor.value) && cursor.value.uid === userId) cursor.delete()
        cursor.continue()
      }
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('indexedDB cursor failed'))
    })
  } catch {
    /* IDB indisponível */
  }
}

/** Adaptador cifrado usado pelos stores zustand persistidos no IndexedDB. */
export const encryptedIndexedDBStorage: AsyncStateStorage = {
  getItem: async (name) => {
    try {
      return await readDecrypted(name)
    } catch {
      return null
    }
  },
  setItem: async (name, value) => {
    try {
      const userId = await resolveActiveUser()
      if (!userId || !isPersistenceCryptoAvailable()) return
      await writeEncrypted(userId, name, value)
    } catch {
      // best-effort: persistence must not break app flows
    }
  },
  removeItem: async (name) => {
    try {
      await idbDel(name)
    } catch {
      // best-effort
    }
  },
}

const indexedDBStorage = encryptedIndexedDBStorage

export const indexedDBJSON = createJSONStorage(() =>
  typeof window !== 'undefined' ? indexedDBStorage : fallbackStorage,
)

export const clearSynapPersistedState = async (storageKeys: string[]): Promise<void> => {
  if (typeof window === 'undefined') return

  await Promise.all(
    storageKeys.map(async (key) => {
      try {
        window.localStorage.removeItem(key)
      } catch {
        /* ignore */
      }
      try {
        await indexedDBStorage.removeItem(key)
      } catch {
        /* ignore */
      }
    }),
  )
}

